/**
 * Audio Upload Component
 *
 * Provides a drag-and-drop audio file upload interface with:
 * - File validation (type, size, duration)
 * - Audio preview with waveform visualization
 * - Recording duration display
 * - Playback controls
 *
 * Used for uploading reference audio for voice cloning.
 */

import { useState, useRef, useCallback, useEffect } from 'react';

// =============================================================================
// Types
// =============================================================================

/**
 * Audio file metadata
 */
export interface AudioMetadata {
  /** File name */
  name: string;
  /** File size in bytes */
  size: number;
  /** MIME type */
  type: string;
  /** Duration in seconds */
  duration: number;
}

/**
 * Upload result
 */
export interface AudioUploadResult {
  /** The uploaded file */
  file: File;
  /** Audio metadata */
  metadata: AudioMetadata;
  /** Object URL for preview */
  previewUrl: string;
}

/**
 * Component props
 */
export interface AudioUploadProps {
  /** Called when a valid audio file is uploaded */
  onUpload: (result: AudioUploadResult) => void;
  /** Called when the audio is removed */
  onRemove?: () => void;
  /** Current audio file (for controlled component) */
  value?: AudioUploadResult | null;
  /** Accepted audio types */
  acceptedTypes?: string[];
  /** Maximum file size in bytes (default 10MB) */
  maxSizeBytes?: number;
  /** Minimum duration in seconds (default 3s) */
  minDurationSec?: number;
  /** Maximum duration in seconds (default 300s) */
  maxDurationSec?: number;
  /** Component disabled state */
  disabled?: boolean;
  /** Show help text */
  showHelp?: boolean;
  /** Custom error message */
  error?: string;
}

// =============================================================================
// Constants
// =============================================================================

const DEFAULT_ACCEPTED_TYPES = ['audio/wav', 'audio/mp3', 'audio/mpeg', 'audio/ogg', 'audio/webm'];
const DEFAULT_MAX_SIZE_BYTES = 10 * 1024 * 1024; // 10MB
const DEFAULT_MIN_DURATION_SEC = 3;
const DEFAULT_MAX_DURATION_SEC = 300;

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Format file size for display
 */
function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Format duration for display
 */
function formatDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

/**
 * Get audio duration from file
 */
async function getAudioDuration(file: File): Promise<number> {
  return new Promise((resolve, reject) => {
    const audio = new Audio();
    const objectUrl = URL.createObjectURL(file);

    audio.addEventListener('loadedmetadata', () => {
      URL.revokeObjectURL(objectUrl);
      resolve(audio.duration);
    });

    audio.addEventListener('error', () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('Failed to load audio file'));
    });

    audio.src = objectUrl;
  });
}

// =============================================================================
// Sub-components
// =============================================================================

/**
 * Audio preview player component
 */
interface AudioPreviewProps {
  previewUrl: string;
  metadata: AudioMetadata;
  onRemove: () => void;
  disabled?: boolean;
}

function AudioPreview({ previewUrl, metadata, onRemove, disabled }: AudioPreviewProps) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);

  const handlePlayPause = () => {
    if (!audioRef.current) return;

    if (isPlaying) {
      audioRef.current.pause();
    } else {
      void audioRef.current.play();
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const handleEnded = () => {
    setIsPlaying(false);
    setCurrentTime(0);
  };

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const time = parseFloat(e.target.value);
    if (audioRef.current) {
      audioRef.current.currentTime = time;
      setCurrentTime(time);
    }
  };

  return (
    <div className="rounded-lg bg-discord-darkest border border-discord-light p-4">
      <audio
        ref={audioRef}
        src={previewUrl}
        onTimeUpdate={handleTimeUpdate}
        onEnded={handleEnded}
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
      />

      {/* File Info */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 min-w-0">
          <svg
            className="h-5 w-5 flex-shrink-0 text-discord-blurple"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3"
            />
          </svg>
          <div className="min-w-0">
            <p className="text-sm font-medium text-white truncate">{metadata.name}</p>
            <p className="text-xs text-gray-500">
              {formatFileSize(metadata.size)} • {formatDuration(metadata.duration)}
            </p>
          </div>
        </div>
        <button
          onClick={onRemove}
          disabled={disabled}
          className="rounded-md p-1.5 text-gray-400 hover:text-red-400 hover:bg-red-900/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          title="Remove audio"
        >
          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>

      {/* Playback Controls */}
      <div className="flex items-center gap-3">
        <button
          onClick={handlePlayPause}
          disabled={disabled}
          className="rounded-full bg-discord-blurple p-2 text-white hover:bg-discord-blurple/80 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          title={isPlaying ? 'Pause' : 'Play'}
        >
          {isPlaying ? (
            <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
              <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
            </svg>
          ) : (
            <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
              <path d="M8 5v14l11-7z" />
            </svg>
          )}
        </button>

        <div className="flex-1 flex items-center gap-2">
          <span className="text-xs text-gray-500 w-10 text-right">
            {formatDuration(currentTime)}
          </span>
          <input
            type="range"
            min="0"
            max={metadata.duration}
            step="0.1"
            value={currentTime}
            onChange={handleSeek}
            disabled={disabled}
            className="flex-1 h-1.5 bg-discord-light rounded-full appearance-none cursor-pointer accent-discord-blurple disabled:cursor-not-allowed"
          />
          <span className="text-xs text-gray-500 w-10">
            {formatDuration(metadata.duration)}
          </span>
        </div>
      </div>
    </div>
  );
}

/**
 * Drop zone component
 */
interface DropZoneProps {
  isDragging: boolean;
  acceptedTypes: string[];
  disabled?: boolean;
  onDrop: (files: FileList) => void;
  onDragOver: (e: React.DragEvent) => void;
  onDragLeave: () => void;
  onClick: () => void;
}

function DropZone({
  isDragging,
  acceptedTypes: _acceptedTypes,
  disabled,
  onDrop,
  onDragOver,
  onDragLeave,
  onClick,
}: DropZoneProps) {
  // Note: _acceptedTypes available for future use in UI display
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled && e.dataTransfer.files.length > 0) {
      onDrop(e.dataTransfer.files);
    }
    onDragLeave();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick();
    }
  };

  return (
    <div
      onClick={disabled ? undefined : onClick}
      onKeyDown={disabled ? undefined : handleKeyDown}
      onDrop={handleDrop}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      role="button"
      tabIndex={disabled ? -1 : 0}
      className={`relative flex flex-col items-center justify-center rounded-lg border-2 border-dashed p-8 transition-colors ${
        isDragging
          ? 'border-discord-blurple bg-discord-blurple/10'
          : disabled
            ? 'border-discord-light bg-discord-darker cursor-not-allowed opacity-50'
            : 'border-discord-light bg-discord-darker hover:border-discord-blurple hover:bg-discord-blurple/5 cursor-pointer'
      }`}
    >
      <svg
        className={`h-12 w-12 mb-3 ${isDragging ? 'text-discord-blurple' : 'text-gray-500'}`}
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.5}
          d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"
        />
      </svg>
      <p className="text-sm font-medium text-white mb-1">
        {isDragging ? 'Drop audio file here' : 'Drop audio file or click to upload'}
      </p>
      <p className="text-xs text-gray-500">
        Supported: WAV, MP3, OGG, WebM
      </p>
      <p className="text-xs text-gray-500 mt-1">
        Max {formatFileSize(DEFAULT_MAX_SIZE_BYTES)} •{' '}
        {DEFAULT_MIN_DURATION_SEC}s - {DEFAULT_MAX_DURATION_SEC / 60}min
      </p>
    </div>
  );
}

// =============================================================================
// Main Component
// =============================================================================

/**
 * Audio upload component with drag-and-drop and preview
 *
 * @example
 * ```tsx
 * const [audio, setAudio] = useState<AudioUploadResult | null>(null);
 *
 * <AudioUpload
 *   value={audio}
 *   onUpload={setAudio}
 *   onRemove={() => setAudio(null)}
 * />
 * ```
 */
export function AudioUpload({
  onUpload,
  onRemove,
  value,
  acceptedTypes = DEFAULT_ACCEPTED_TYPES,
  maxSizeBytes = DEFAULT_MAX_SIZE_BYTES,
  minDurationSec = DEFAULT_MIN_DURATION_SEC,
  maxDurationSec = DEFAULT_MAX_DURATION_SEC,
  disabled,
  showHelp = true,
  error: externalError,
}: AudioUploadProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Clean up preview URL on unmount or when value changes
  useEffect(() => {
    return () => {
      if (value?.previewUrl) {
        URL.revokeObjectURL(value.previewUrl);
      }
    };
  }, [value?.previewUrl]);

  /**
   * Validate and process uploaded file
   */
  const processFile = useCallback(
    async (file: File) => {
      setError(null);
      setIsProcessing(true);

      try {
        // Validate file type
        if (!acceptedTypes.includes(file.type)) {
          setError(`Invalid file type. Supported: WAV, MP3, OGG, WebM`);
          return;
        }

        // Validate file size
        if (file.size > maxSizeBytes) {
          setError(`File too large. Maximum size: ${formatFileSize(maxSizeBytes)}`);
          return;
        }

        // Get and validate duration
        const duration = await getAudioDuration(file);

        if (duration < minDurationSec) {
          setError(`Audio too short. Minimum duration: ${minDurationSec} seconds`);
          return;
        }

        if (duration > maxDurationSec) {
          setError(
            `Audio too long. Maximum duration: ${formatDuration(maxDurationSec)}`
          );
          return;
        }

        // Create preview URL and metadata
        const previewUrl = URL.createObjectURL(file);
        const metadata: AudioMetadata = {
          name: file.name,
          size: file.size,
          type: file.type,
          duration,
        };

        // Clean up old preview URL if exists
        if (value?.previewUrl) {
          URL.revokeObjectURL(value.previewUrl);
        }

        onUpload({ file, metadata, previewUrl });
      } catch (err) {
        setError('Failed to process audio file. Please try a different file.');
      } finally {
        setIsProcessing(false);
      }
    },
    [acceptedTypes, maxSizeBytes, minDurationSec, maxDurationSec, onUpload, value?.previewUrl]
  );

  /**
   * Handle file input change
   */
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      void processFile(files[0]);
    }
    // Reset input value so same file can be selected again
    e.target.value = '';
  };

  /**
   * Handle file drop
   */
  const handleDrop = (files: FileList) => {
    if (files.length > 0) {
      void processFile(files[0]);
    }
  };

  /**
   * Handle drag events
   */
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  /**
   * Handle click to open file dialog
   */
  const handleClick = () => {
    fileInputRef.current?.click();
  };

  /**
   * Handle remove
   */
  const handleRemove = () => {
    if (value?.previewUrl) {
      URL.revokeObjectURL(value.previewUrl);
    }
    onRemove?.();
    setError(null);
  };

  const displayError = externalError || error;

  return (
    <div className="space-y-3">
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept={acceptedTypes.join(',')}
        onChange={handleFileChange}
        disabled={disabled || isProcessing}
        className="hidden"
      />

      {/* Loading state */}
      {isProcessing && (
        <div className="flex items-center justify-center rounded-lg bg-discord-darker border border-discord-light p-8">
          <div className="flex items-center gap-3">
            <svg
              className="h-5 w-5 animate-spin text-discord-blurple"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            <span className="text-sm text-gray-300">Processing audio...</span>
          </div>
        </div>
      )}

      {/* Preview or Upload Zone */}
      {!isProcessing && (
        value ? (
          <AudioPreview
            previewUrl={value.previewUrl}
            metadata={value.metadata}
            onRemove={handleRemove}
            disabled={disabled}
          />
        ) : (
          <DropZone
            isDragging={isDragging}
            acceptedTypes={acceptedTypes}
            disabled={disabled}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onClick={handleClick}
          />
        )
      )}

      {/* Error message */}
      {displayError && (
        <div className="flex items-start gap-2 rounded-md bg-red-900/30 border border-red-500/50 px-3 py-2">
          <svg
            className="h-4 w-4 flex-shrink-0 text-red-400 mt-0.5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <p className="text-sm text-red-200">{displayError}</p>
        </div>
      )}

      {/* Help text */}
      {showHelp && !value && !displayError && (
        <p className="text-xs text-gray-500">
          Upload a clear audio recording for voice cloning. Best results with 10-30 seconds
          of natural speech in a quiet environment.
        </p>
      )}
    </div>
  );
}

export default AudioUpload;
